/**
 * Flow Temperature Band Estimation Module
 * 
 * Derives required flow temperature bands for heat pumps based on:
 * - Emitter low-temp suitability
 * - Pipe bore constraints
 * - Rooms struggling to heat
 * - Radiator sizing
 * - Underfloor heating presence
 * - Heat loss magnitude
 * 
 * Maps these factors to flow temperature bands, which then map to realistic SPF ranges.
 */

import type { SystemRecInput } from './types';

/**
 * Flow temperature bands (°C)
 * These represent the likely operating range needed for the heating system
 */
export type FlowTempBand = '35_45' | '45_55' | '55_65' | '65_70';

/**
 * SPF (Seasonal Performance Factor) range with confidence
 */
export interface SPFRange {
  min: number;
  max: number;
  confidence: 'low' | 'medium' | 'high';
  flowBand: FlowTempBand;
  reasons: string[];
}

/**
 * Lookup table: flow temperature band → SPF range
 * 
 * Based on real-world heat pump performance:
 * - Lower flow temps = higher COP/SPF (better Carnot efficiency)
 * - Higher flow temps = lower COP/SPF (worse Carnot efficiency)
 */
const FLOW_BAND_TO_SPF: Record<FlowTempBand, { min: number; max: number }> = {
  '35_45': { min: 3.5, max: 4.8 },  // Low-temp ready (UFH / oversized rads)
  '45_55': { min: 2.8, max: 3.6 },  // Borderline / mixed
  '55_65': { min: 2.0, max: 2.9 },  // High-temp requirement (undersized rads)
  '65_70': { min: 1.6, max: 2.2 },  // Likely unsuitable without upgrades
};

/**
 * Derive required flow temperature band from input constraints
 * 
 * Logic (rules-based, not magic):
 * 1. Start with baseline assumption (45_55 for typical UK radiators)
 * 2. If emitterLowTempSuitability = definitely_needs_upgrade → minimum 55_65
 * 3. If pipeBore = 15mm AND roomsStruggle = yes → push up one band
 * 4. If radiatorSize = oversized OR UFH extent > none → pull down one band
 * 5. If pipeBore = 8mm/10mm (microbore) → push up one band
 * 
 * @param input - Normalized system recommendation input
 * @param heatLossKw - Design heat loss (optional, for future refinement)
 * @returns Flow temperature band
 */
export function deriveFlowTempBand(
  input: SystemRecInput,
  heatLossKw?: number
): { band: FlowTempBand; reasons: string[] } {
  const reasons: string[] = [];
  
  // Start with baseline: 45-55°C (typical UK radiator system)
  let bandIndex = 1; // 0=35_45, 1=45_55, 2=55_65, 3=65_70
  const bands: FlowTempBand[] = ['35_45', '45_55', '55_65', '65_70'];
  
  reasons.push('Starting assumption: 45-55°C for typical radiators');
  
  // Extract emitter info
  const emitterSuitability = input.emitters?.lowTempSuitability;
  const roomsStruggle = input.emitters?.roomsStruggleToHeat;
  const pipeBore = input.emitters?.pipeBore;
  const emitterType = input.emitters?.type;
  
  // Rule 1: Emitter suitability is critical
  if (emitterSuitability === 'definitely_needs_upgrade') {
    // Force to at least 55-65°C band
    if (bandIndex < 2) {
      bandIndex = 2;
      reasons.push('Emitters definitely need upgrade → minimum 55-65°C required');
    }
  } else if (emitterSuitability === 'likely_needs_upgrade') {
    // Push up one band (but not as severe as "definitely")
    bandIndex = Math.min(3, bandIndex + 1);
    reasons.push('Emitters likely need upgrade → flow temp increased');
  } else if (emitterSuitability === 'likely_ok') {
    // Can potentially use lower temps - but don't force it yet
    reasons.push('Emitters likely suitable for low temps');
  }
  
  // Rule 2: Pipe bore + rooms struggling = constraint
  if (pipeBore === '15mm' && roomsStruggle === 'yes') {
    bandIndex = Math.min(3, bandIndex + 1);
    reasons.push('15mm pipes + rooms struggle → need higher flow temp for adequate flow');
  }
  
  // Rule 3: Microbore (8mm/10mm) = severe constraint
  if (pipeBore === '8mm' || pipeBore === '10mm') {
    bandIndex = Math.min(3, bandIndex + 1);
    reasons.push(`${pipeBore} microbore pipes → severe flow restriction, higher temps needed`);
  }
  
  // Rule 4: Underfloor heating = excellent for low temps
  if (emitterType === 'underfloor') {
    bandIndex = Math.max(0, bandIndex - 1);
    reasons.push('Underfloor heating → can use low flow temps (35-45°C)');
  } else if (emitterType === 'mixed') {
    // Mixed = some UFH benefit
    bandIndex = Math.max(0, bandIndex - 1);
    reasons.push('Mixed emitters (some UFH) → can reduce flow temps');
  }
  
  // Rule 5: Oversized radiators (explicit signal)
  // Note: Currently inferred from likely_ok + no rooms struggling; explicit radiatorSize field could improve accuracy
  if (emitterSuitability === 'likely_ok' && roomsStruggle === 'no') {
    // Good signal that emitters are adequate/oversized
    if (emitterType === 'radiators') {
      bandIndex = Math.max(0, bandIndex - 1);
      reasons.push('Emitters suitable + no rooms struggling → likely oversized, can reduce flow temps');
    }
  }
  
  // Rule 6: High heat loss = likely needs higher temps (unless emitters are oversized)
  // This is a subtle effect - only apply if we have heat loss data and emitters aren't confirmed good
  if (heatLossKw && heatLossKw > 12 && emitterSuitability !== 'likely_ok') {
    reasons.push(`High heat loss (${heatLossKw.toFixed(1)} kW) → may need higher flow temps`);
    // Don't automatically push up, but note it
  }
  
  // Clamp to valid range
  bandIndex = Math.max(0, Math.min(3, bandIndex));
  
  return {
    band: bands[bandIndex],
    reasons,
  };
}

/**
 * Calculate SPF range from flow temperature band with modifiers
 * 
 * Applies small adjustments based on:
 * - Occupancy timing (steady vs bursty heating)
 * - Cycling risk factors
 * 
 * @param flowBand - Flow temperature band
 * @param input - System input for modifier calculations
 * @returns SPF range with confidence
 */
export function calculateSPFRange(
  flowBand: FlowTempBand,
  input: SystemRecInput
): SPFRange {
  const baseRange = FLOW_BAND_TO_SPF[flowBand];
  let min = baseRange.min;
  let max = baseRange.max;
  const reasons: string[] = [];
  
  // Modifier 1: Occupancy timing affects cycling and steady-state operation
  const occupancyTiming = input.occupancyTiming;
  
  if (occupancyTiming === 'most_of_day') {
    // Home all day = steadier heating = better for heat pump efficiency
    const uplift = 0.2;
    min += uplift;
    max += uplift;
    reasons.push(`Occupied most of day → steadier heating (+${uplift.toFixed(1)} SPF)`);
  } else if (occupancyTiming === 'evenings_weekends') {
    // Home only evenings/weekends = bursty heating = worse for heat pump
    const penalty = 0.2;
    min -= penalty;
    max -= penalty;
    reasons.push(`Occupied evenings/weekends only → bursty heating (-${penalty.toFixed(1)} SPF)`);
  }
  
  // Modifier 2: Poor insulation can reduce efficiency due to higher flow temps
  const insulationLevel = input.insulationLevel;
  
  if (insulationLevel === 'poor') {
    // Poor insulation = higher heat loss = may need higher flow temps in practice
    const penalty = 0.1;
    min -= penalty;
    max -= penalty;
    reasons.push(`Poor insulation → higher flow temps likely (-${penalty.toFixed(1)} SPF)`);
  } else if (insulationLevel === 'high') {
    // Good insulation = lower heat loss = can maintain lower flow temps
    const uplift = 0.1;
    min += uplift;
    max += uplift;
    reasons.push(`Good insulation → lower flow temps achievable (+${uplift.toFixed(1)} SPF)`);
  }
  
  // Clamp to sensible bounds
  // Minimum 1.5 (worse would be silly - just use resistance heating)
  // Maximum 5.0 (realistic upper bound even in ideal conditions)
  min = Math.max(1.5, Math.min(5.0, min));
  max = Math.max(1.5, Math.min(5.0, max));
  
  // Ensure min <= max
  if (min > max) {
    const avg = (min + max) / 2;
    min = avg - 0.2;
    max = avg + 0.2;
  }
  
  // Determine confidence based on flow band and input completeness
  let confidence: 'low' | 'medium' | 'high' = 'medium';
  
  // High confidence: good emitter data + known pipe bore + low flow temps
  if (
    input.emitters?.lowTempSuitability !== 'unknown' &&
    input.emitters?.pipeBore !== 'unknown' &&
    flowBand === '35_45'
  ) {
    confidence = 'high';
  }
  
  // Low confidence: unknown emitters or high flow temps
  if (
    input.emitters?.lowTempSuitability === 'unknown' ||
    flowBand === '65_70'
  ) {
    confidence = 'low';
  }
  
  return {
    min: Math.round(min * 10) / 10, // Round to 1 decimal
    max: Math.round(max * 10) / 10,
    confidence,
    flowBand,
    reasons,
  };
}

/**
 * Get effective SPF range for heat pump performance estimation
 * 
 * This is the main entry point that combines flow temp band derivation
 * with SPF range calculation.
 * 
 * @param input - System recommendation input
 * @param heatLossKw - Optional heat loss for refinement
 * @returns SPF range with full reasoning
 */
export function getEffectiveSPFRange(
  input: SystemRecInput,
  heatLossKw?: number
): SPFRange {
  const { band, reasons: flowReasons } = deriveFlowTempBand(input, heatLossKw);
  const spfRange = calculateSPFRange(band, input);
  
  // Combine reasons
  return {
    ...spfRange,
    reasons: [...flowReasons, ...spfRange.reasons],
  };
}
